import { nanoid } from "nanoid";

import {
  CancelDownloadCommand,
  Command,
  CommandName,
  CompletedDownloadCommandCallback,
  DisposeCommand,
  DownloadCommand,
  DownloadCommandCallback,
  DownloadCommandCallbackName,
  DownloadManagerError,
  DownloadManagerErrorName,
  ProgressDownloadCommandCallback,
} from "../../workers/DownloadManager/types";
// The worker instance is instantiated in another file because Jest does not understand
// `import.meta.url` yet!(https://github.com/facebook/jest/issues/11167). This way we
// can mock the worker without Jest trying to compile `import.meta.url`.
// See `jestGlobalSetup.js`.
import { worker } from "../../workers/DownloadManager/worker";
import {
  CacheEntryExpiredEvent,
  CacheEntryExpiringEvent,
  CacheEntryPriority,
  MemoryCache,
} from "../MemoryCache";
import { listToMap } from "./listToMap";
import { nameof } from "./nameof";
import {
  GetMediaResult,
  LoadMediaItem,
  LoadMediaResult,
  PreloadMediaResult,
} from "./types";

type DownloadResult = {
  url: string;
  result?: { downloadedUrl: string; size: number };
  error?: DownloadManagerError;
};

export class MediaManager {
  private static s_instance: MediaManager;

  private readonly _cache = MemoryCache.instance;
  private readonly _downloadManagerWorker = worker;

  // Keeps track of media that are currently being displayed and should not
  // be expired by the cache.
  private readonly _references = new Map<string, number>();

  // Keeps track of media that are currently being preloaded and should not
  // be expired by the cache.
  private readonly _preloadUrls = new Map<string, number>();

  private constructor() {
    this._cache
      .on("expiring", this.handleCacheExpiring.bind(this))
      .on("expired", this.handleCacheExpired.bind(this));
  }

  public static get instance() {
    if (!MediaManager.s_instance) {
      MediaManager.s_instance = new MediaManager();
    }

    return MediaManager.s_instance;
  }

  private handleCacheExpiring(event: CacheEntryExpiringEvent) {
    const url = event.key as string;

    if (this._references.has(url) || this._preloadUrls.has(url)) {
      // prevent the cache from expiring the entry for this media
      event.cancel();
    }
  }

  private handleCacheExpired(event: CacheEntryExpiredEvent) {
    const url = event.key as string;

    this._downloadManagerWorker.postMessage({
      name: CommandName.DISPOSE,
      url,
    } as DisposeCommand);
  }

  private acquireReference(url: string) {
    const currentCount = this._references.get(url) ?? 0;
    this._references.set(url, currentCount + 1);
  }

  private releaseReference(url: string) {
    if (!this._references.has(url)) {
      return;
    }

    const currentCount = this._references.get(url);
    const newCount = currentCount - 1;

    if (newCount === 0) {
      this._references.delete(url);
    } else {
      this._references.set(url, newCount);
    }
  }

  private acquirePreloadUrls(urls: Array<string>) {
    for (const url of urls) {
      const currentCount = this._preloadUrls.get(url) ?? 0;
      this._preloadUrls.set(url, currentCount + 1);
    }
  }

  private releasePreloadUrls(urls: Array<string>) {
    for (const url of urls) {
      if (!this._preloadUrls.has(url)) {
        continue;
      }

      const currentCount = this._preloadUrls.get(url);
      const newCount = currentCount - 1;

      if (newCount === 0) {
        this._preloadUrls.delete(url);
      } else {
        this._preloadUrls.set(url, newCount);
      }
    }
  }

  // Interacts with the DownloadManager through the DownloadManagerWorker and exposes everything as
  // a Promise.
  private download(
    urls: Array<string>,
    priority: number,
    abortSignal: AbortSignal,
    progressCallback?: (progress: number) => void
  ): Promise<Array<DownloadResult>> {
    return new Promise<Array<DownloadResult>>((resolve, reject) => {
      if (abortSignal.aborted) {
        reject(new DOMException("Download was aborted.", "AbortError"));
        return;
      }

      const downloadId = nanoid();

      const listener = (event: MessageEvent) => {
        const callback = event.data as DownloadCommandCallback;
        if (callback.downloadId !== downloadId) {
          return;
        }

        switch (callback.name) {
          case DownloadCommandCallbackName.PROGRESS: {
            if (progressCallback) {
              progressCallback(
                (callback as ProgressDownloadCommandCallback).progress
              );
            }
            break;
          }
          case DownloadCommandCallbackName.COMPLETED: {
            this._downloadManagerWorker.removeEventListener(
              "message",
              listener
            );
            resolve((callback as CompletedDownloadCommandCallback).items);
            break;
          }
          default:
            console.warn(
              `Received unknown download command callback '${callback.name}'; ignoring`,
              event
            );
            break;
        }
      };
      this._downloadManagerWorker.addEventListener("message", listener);

      this._downloadManagerWorker.postMessage({
        name: CommandName.DOWNLOAD,
        downloadId,
        urls,
        priority,
        shouldReportProgress: !!progressCallback,
      } as DownloadCommand);

      abortSignal.addEventListener("abort", () => {
        this._downloadManagerWorker.removeEventListener("message", listener);

        this._downloadManagerWorker.postMessage({
          name: CommandName.CANCEL_DOWNLOAD,
          downloadId,
        } as CancelDownloadCommand);

        reject(new DOMException("Download was aborted.", "AbortError"));
      });
    });
  }

  private setOrUpdateCacheEntry(
    url: string,
    downloadedUrl: string,
    priority: CacheEntryPriority,
    size: number
  ) {
    if (!this._cache.has(url)) {
      this._cache.set(url, downloadedUrl, priority, size);
    } else {
      const cachedDownloadedUrl = this._cache.get(url);

      //IDEA: revoke `downloadedUrl` and use `cachedDownloadedUrl` instead to prevent
      //      revoking blobs in use
      if (cachedDownloadedUrl !== downloadedUrl) {
        this._cache.set(url, downloadedUrl, priority, size);
      }
    }
  }

  /**
   * Gets a (previously loaded) media and returns it as a GetMediaResult.
   *
   * @param url the URL of the media to load
   */
  public get(url: string): GetMediaResult {
    const src = this._cache.get(url);
    if (!src) {
      return;
    }

    this.acquireReference(url);
    return { src, release: () => this.releaseReference(url) };
  }

  /**
   * Loads (downloads and caches) an item and returns it as a LoadMediaResult.
   *
   * @param item the item to load
   * @param fallbackUrl the fallback URL to use if media fails to load
   * @param loadPriority the priority to load with
   */
  public load(
    item: LoadMediaItem,
    fallbackUrl: string,
    loadPriority = 0
  ): LoadMediaResult {
    this.acquireReference(item.url);

    const src = this._cache.get(item.url);
    if (src) {
      return {
        srcPromise: Promise.resolve(src),
        release: () => this.releaseReference(item.url),
        cancel: () => {},
      };
    }

    const abortController = new AbortController();
    const downloadPromise = this.download(
      [item.url],
      loadPriority,
      abortController.signal
    );

    const srcPromise = new Promise<string>((resolve, reject) => {
      downloadPromise
        .then(([{ error, result }]) => {
          if (!error) {
            this.setOrUpdateCacheEntry(
              item.url,
              result.downloadedUrl,
              item.cachePriority,
              result.size
            );

            resolve(result.downloadedUrl);
          } else {
            if (error.name !== DownloadManagerErrorName.CANCEL) {
              this.setOrUpdateCacheEntry(
                item.url,
                fallbackUrl,
                item.cachePriority,
                new Blob([fallbackUrl]).size
              );
            }

            resolve(fallbackUrl);
          }
        })
        .catch((error) => reject(error));
    });

    return {
      srcPromise,
      release: () => this.releaseReference(item.url),
      cancel: () => abortController.abort(),
    };
  }

  /**
   * Preloads (downloads and caches) multiple items and returns a PreloadMediaResult.
   *
   * @param items the items to load
   * @param loadPriority the priority to load with
   * @param progressCallback the callback to use for reporting progress updates
   */
  public preload(
    items: Array<LoadMediaItem>,
    loadPriority = 0,
    progressCallback?: (progress: number) => void
  ): PreloadMediaResult {
    const itemsToLoad = items.filter((item) => !this._cache.has(item.url));
    if (itemsToLoad.length === 0) {
      return;
    }

    const cachePriorities: Map<string, LoadMediaItem> = listToMap(
      itemsToLoad,
      nameof<LoadMediaItem>("url")
    );

    const urlsToLoad = itemsToLoad.map(({ url }) => url);

    this.acquirePreloadUrls(urlsToLoad);

    const abortController = new AbortController();
    const promise = this.download(
      urlsToLoad,
      loadPriority,
      abortController.signal,
      progressCallback
    );

    const resultPromise = promise
      .then((downloadResults) => {
        for (const { url, error, result } of downloadResults) {
          if (error) {
            continue;
          }

          const cachePriority = cachePriorities.get(url).cachePriority;
          this.setOrUpdateCacheEntry(
            url,
            result.downloadedUrl,
            cachePriority,
            result.size
          );
        }
      })
      .finally(() => {
        this.releasePreloadUrls(urlsToLoad);
      });

    return {
      promise: resultPromise,
      cancel: () => abortController.abort(),
    };
  }

  public terminateAllDownloads() {
    this._downloadManagerWorker.postMessage({
      name: CommandName.TERMINATE_ALL_DOWNLOADS,
    } as Command);
  }
}
